import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiDigitalAuthAuthentication } from '../../services/auth/authentication.service';
import { authController, authLogin } from '../../scripts/authentication.script';
import { removeCookies } from '../../scripts/cookies.script';

export interface HomePageProps {};

const HomePage: React.FunctionComponent<HomePageProps> = props => {
  const navigate = useNavigate()
  const handled = useRef(false)

  useEffect( () => {
    if (handled.current) return
    handled.current = true

    const urlParams = new URLSearchParams(window.location.search)
    const accessTokenFromUrl = urlParams.get('access_token')
    const refreshTokenFromUrl = urlParams.get('refresh_token')

    if (accessTokenFromUrl && refreshTokenFromUrl) {
      handleExternalTokens(accessTokenFromUrl, refreshTokenFromUrl)
      return
    }

    if (authController()) {
      navigate('/main/dashboard', {replace: true})
    } else {
      navigate('/auth/login', {replace: true})
    }
  }, [navigate])

  const handleExternalTokens = async (accessToken: string, refreshToken: string) => {
    // 1. Clean URL immediately — remove tokens from browser history
    window.history.replaceState({}, document.title, window.location.pathname)

    // 2. Decode the JWT to extract user/company context
    let jwtPayload: any = {}
    try {
      const parts = accessToken.split('.')
      if (parts.length === 3) {
        jwtPayload = JSON.parse(atob(parts[1]))
      }
    } catch {
      // Invalid JWT — fall through to login
      navigate('/auth/login', {replace: true})
      return
    }

    // 3. Call refresh to get new tokens + companies + subscription
    const apiKey = process.env.REACT_APP_SERVICE_API_KEY ?? ''
    try {
      const response = await ApiDigitalAuthAuthentication.refresh(apiKey, refreshToken)

      if (response.status === 200 && response.data) {
        const data = response.data

        // 4. Clear any existing session
        removeCookies()

        // 5. Store cookies via authLogin — single source of truth
        // Note: refresh response returns partial user (locale only),
        // so we build a full UserToken from the JWT payload.
        authLogin({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_in: data.expires_in ?? 900,
          user: {
            id: jwtPayload.userId ?? '',
            uuid: '',
            avatar: null,
            first_name: '',
            last_name: '',
            email: jwtPayload.email ?? '',
            locale: data.user?.locale ?? 'en',
            verified: true,
            new_user: false,
          },
          app_service: data.app_service ?? {
            id: jwtPayload.serviceId ?? '',
            api_key: apiKey,
            name: '',
            url: '',
          },
          company: data.company ?? null,
          companies: data.companies ?? [],
          subscription: data.subscription ?? null,
          bo_modules: data.bo_modules ?? [],
        } as any)

        // 7. Full reload to pick up cookies and initialize the app
        window.location.href = '/main/dashboard'
      } else {
        // Refresh failed — redirect to login
        navigate('/auth/login', {replace: true})
      }
    } catch {
      // Network error or invalid refresh token — redirect to login
      navigate('/auth/login', {replace: true})
    }
  }

  return null;
};

export default HomePage;
